import React, { useState, useEffect } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import { Table, TableRow } from '@material-ui/core';
import TableComponents from 'components/Table';
import { Button } from 'components/Base';
import moment from 'moment';
import ArrowRightAltRoundedIcon from '@material-ui/icons/ArrowRightAltRounded';
import {
  ScatterChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Scatter,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ZAxis
} from 'recharts';
import { formatPercent } from 'utils';
import TooltipRiskScatterChart from 'components/Charts/Tooltips/RiskScatterChart';
import Warning from 'components/Charts/Utils/Warning';
import MorningstarCopyright from 'components/MorningstarCopyright/MorningstarCopyright';
import { useTheme } from '@material-ui/styles';
import { formatName } from 'components/Charts/Utils/benchmarks';

const { selectors, actions } = Store;

interface RiskTabContentProps {
  data?: any;
  performanceData?: any;
  isForReport?: boolean;
  updateRiskPerformancePeriod: Function;
  riskPerformancePeriod: string;
  setReportData?: Function;
  height?: number;
  hasDateControl?: boolean;
}

const RiskTabContent = ({
  data,
  performanceData,
  isForReport = false,
  updateRiskPerformancePeriod,
  riskPerformancePeriod,
  setReportData,
  height = 400,
  hasDateControl = true
}: RiskTabContentProps) => {
  const theme = useTheme() as any;
  // const [performancePeriod, setPerformancePeriod] = useState('1y');
  const [startDate, setStartDate] = useState('');
  const [endDate] = useState(moment().format('DD/MM/YYYY'));
  const [hasData, setHasData] = useState(true);

  const [mappedData, setMappedData] = useState([]);

  const performancePeriods = ['1y', '3y', '5y'];

  useEffect(() => {
    const number = (riskPerformancePeriod.match(/\d+/gm) || ['5'])[0] as string;
    const type = ({ m: 'month', y: 'year' } as any)[
      number ? riskPerformancePeriod.split(number)[1] : 'y'
    ];

    setStartDate(
      moment()
        .subtract(Number(number), type)
        .add(1, 'days')
        .format('DD/MM/YYYY')
    );

    setMappedData(
      data.map((investment: any, index: number) => {
        const prefix = ({ '1': 'one', '3': 'three', '5': 'five' } as any)[
          number
        ];

        const standardDeviationKey = `${prefix}_y_standard_deviation`;
        const figure = (performanceData[investment.name] &&
          performanceData[investment.name][
            `cumulative_${number}${type}_performance`
          ]) || { value: null };

        return {
          name: investment.name,
          color: theme.palette.baseColors.charts['charting'][index],
          standard_deviation: investment[standardDeviationKey]
            ? investment[standardDeviationKey] * 100
            : null,
          performance: figure.value === null ? null : figure.value * 100
        };
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskPerformancePeriod, data]);

  useEffect(() => {
    setHasData(
      mappedData.some(
        (investment: any) =>
          investment.standard_deviation !== null &&
          investment.performance !== null
      )
    );

    if (setReportData && mappedData.length > 0) {
      setReportData(mappedData);
    }
  }, [mappedData]);

  return (
    <>
      {hasDateControl && (
        <div style={{ display: 'flex' }}>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'baseline'
            }}
          >
            Performance period
            {!isForReport && (
              <>
                {performancePeriods.map(period => {
                  return (
                    <Button
                      key={period}
                      style={{
                        height: '24px',
                        paddingLeft: 0,
                        paddingRight: 0,
                        minWidth: '45px'
                      }}
                      color={
                        riskPerformancePeriod === period ? 'primary' : 'default'
                      }
                      gray={riskPerformancePeriod === period ? false : true}
                      onClick={() => updateRiskPerformancePeriod(period)}
                    >
                      {period}
                    </Button>
                  );
                })}
              </>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: '0 0 1rem 1rem'
              }}
            >
              {startDate}
              <ArrowRightAltRoundedIcon />
              {endDate}
            </div>
          </div>
        </div>
      )}
      {!hasData ? (
        <Warning text={'No data is available for this performance period'} />
      ) : (
        <>
          <ResponsiveContainer
            width="100%"
            height={isForReport ? 300 : height}
            minWidth={isForReport ? 1500 : ''}
          >
            <ScatterChart
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="standard_deviation"
                name="Annualised Volatility"
                tickFormatter={(val: string) => `${val}%`}
                allowDecimals={false}
                label={{
                  value: 'Annualised Volatility',
                  position: 'bottom'
                }}
              />
              <YAxis
                type="number"
                dataKey="performance"
                name="Cumulative Total Return Performance"
                tickFormatter={(val: string) => `${val}%`}
                allowDecimals={false}
                label={{
                  value: 'Performance',
                  angle: -90,
                  position: 'insideLeft'
                }}
              />

              <ZAxis range={[140, 141]} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={TooltipRiskScatterChart}
              />
              <Scatter data={mappedData}>
                {mappedData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <MorningstarCopyright />
        </>
      )}

      {!isForReport && (
        <div>
          <Table>
            <TableComponents.TableHead
              cells={['', 'Performance', 'Annualised Volatility']}
            />
            <TableComponents.TableBody>
              {mappedData.map((item: any, index: number) => {
                return (
                  <TableRow key={index}>
                    <TableComponents.TableCell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background:
                              theme.palette.baseColors.charts['charting'][
                                index
                              ],
                            marginRight: '0.2rem'
                          }}
                        ></div>
                        {formatName(item.name)}
                      </div>
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      {item.performance ? formatPercent(item.performance) : '-'}
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      {item.standard_deviation
                        ? formatPercent(item.standard_deviation)
                        : '-'}
                    </TableComponents.TableCell>
                  </TableRow>
                );
              })}
            </TableComponents.TableBody>
          </Table>
        </div>
      )}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskPerformancePeriod:
        selectors.investmentAnalyserSelectors.riskPerformancePeriod(state)
    }),
    {
      updateRiskPerformancePeriod:
        actions.investmentAnalyserActions.updateRiskPerformancePeriod
    }
  )
)(RiskTabContent);
